import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiClient } from "@/lib/api/client";
import { DepartmentDto, CreateDepartmentDto, UpdateDepartmentDto, DepartmentFilterParams } from "@/lib/types/api";
import { showToast } from "@/lib/utils/toast";
import { handleApiError } from "@/lib/utils/errors";
import { SEARCH_DEBOUNCE_MS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, Column } from "@/components/datatable/DataTable";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Building2, Search, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { LoadingButton } from "@/components/ui/loading";
import { StatusBadge } from "@/components/ui/status-badge";

const departmentSchema = z.object({
  departmentName: z.string().min(1, "Department name is required"),
  description: z.string().optional(),
});

type DepartmentFormData = z.infer<typeof departmentSchema>;

export default function DepartmentsPage() {
  const [departments, setDepartments] = useState<DepartmentDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Search and sorting states
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortDescending, setSortDescending] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<DepartmentFormData>({
    resolver: zodResolver(departmentSchema),
  });

  // Load departments with current filters
  const loadDepartments = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const filterParams: DepartmentFilterParams = {
        pageNumber: currentPage,
        pageSize: pageSize,
        searchTerm: searchQuery || undefined,
        sortBy: sortBy,
        sortDescending: sortDescending,
      };
      
      const response = await apiClient.getDepartmentsPagedWithFilters(filterParams);
      
      if (response.success && response.data) {
        setDepartments(response.data.items || []);
        setTotalCount(response.data.totalCount || 0);
        setTotalPages(Math.ceil((response.data.totalCount || 0) / pageSize));
      }
    } catch (err: unknown) {
      showToast.error(handleApiError(err, "Failed to load departments"));
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, sortBy, sortDescending]);

  // Single effect to handle all data loading with proper debouncing
  useEffect(() => {
    // For page/pageSize changes, load immediately without debounce
    if (searchQuery === "" && sortBy === "name" && !sortDescending) {
      loadDepartments();
      return;
    }

    // For search/sort changes, debounce
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        loadDepartments();
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [currentPage, pageSize, searchQuery, sortBy, sortDescending, loadDepartments]);

  const onSubmit = async (data: DepartmentFormData) => {
    try {
      setIsSubmitting(true);
      if (editingId) {
        const updateData: UpdateDepartmentDto = {
          departmentName: data.departmentName,
          description: data.description,
          isActive: true,
        };
        await apiClient.updateDepartment(editingId, updateData);
        showToast.success("Department updated successfully");
      } else {
        const createData: CreateDepartmentDto = {
          departmentName: data.departmentName,
          description: data.description,
        };
        await apiClient.createDepartment(createData);
        showToast.success("Department created successfully");
      }
      setShowForm(false);
      setEditingId(null);
      reset();
      loadDepartments();
    } catch (err: unknown) {
      showToast.error(handleApiError(err, editingId ? "Failed to update department" : "Failed to create department"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (department: DepartmentDto) => {
    setEditingId(department.id);
    reset({
      departmentName: department.departmentName,
      description: department.description || "",
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this department?")) return;

    try {
      await apiClient.deleteDepartment(id);
      showToast.success("Department deleted successfully");
      loadDepartments();
    } catch (err: unknown) {
      showToast.error(handleApiError(err, "Failed to delete department"));
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    reset();
  };

  const handleOpenNew = () => {
    setEditingId(null);
    reset({
      departmentName: "",
      description: "",
    });
    setShowForm(true);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDescending(!sortDescending);
    } else {
      setSortBy(column);
      setSortDescending(false);
    }
  };

  return (
    <div className="space-y-6">
      <Dialog open={showForm} onOpenChange={(open) => !open && handleCancel()}>
        <DialogContent onClose={handleCancel}>
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Department" : "New Department"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Input
              label="Department Name"
              placeholder="e.g., Human Resources"
              error={errors.departmentName?.message}
              {...register("departmentName")}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
                placeholder="Department description..."
                {...register("description")}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCancel} disabled={isSubmitting}>
                Cancel
              </Button>
              <LoadingButton type="submit" isLoading={isSubmitting} className="bg-primary px-4 py-2 rounded hover:bg-primary/90">
                {editingId ? "Update" : "Create"}
              </LoadingButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Card className="shadow-sm">
        <CardHeader className="border-b bg-gray-50/50 dark:bg-gray-800/50">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Building2 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <CardTitle className="text-2xl">Departments</CardTitle>
                <CardDescription className="mt-1">Manage and organize your departments</CardDescription>
              </div>
            </div>
            <Button onClick={handleOpenNew} className="shadow-sm">
              <Plus className="mr-2 h-4 w-4" />
              Add Department
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Search and Count Badge */}
          <div className="mb-6 space-y-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Search</h3>
              {totalCount > 0 && (
                <div className="flex items-center gap-2 px-2.5 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                  <span>{totalCount}</span>
                  <span>Total Departments</span>
                </div>
              )}
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search departments..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-10"
              />
            </div>
            {/* Active search chip */}
            {searchQuery && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-600 dark:text-gray-400">Active filters:</span>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md text-xs">
                    Search: {searchQuery}
                    <button onClick={() => setSearchQuery("")} className="hover:text-blue-900 dark:hover:text-blue-100">×</button>
                  </span>
                  <button
                    onClick={() => setSearchQuery("")}
                    className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 underline"
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {(() => {
            const columns: Column<DepartmentDto>[] = [
              {
                key: "name",
                header: "Name",
                sortable: true,
                render: (d) => (
                  <span className="font-medium text-gray-900 dark:text-gray-100">{d.departmentName}</span>
                ),
              },
              {
                key: "description",
                header: "Description",
                render: (d) => (
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {d.description || <span className="text-gray-400">—</span>}
                  </span>
                ),
              },
              {
                key: "status",
                header: "Status",
                render: (d) => <StatusBadge isActive={d.isActive} />,
              },
              {
                key: "createdat",
                header: "Created",
                sortable: true,
                render: (d) => (
                  <span className="text-sm text-gray-500 dark:text-gray-400">{formatDate(d.createdAt)}</span>
                ),
              },
            ];
            return (
              <DataTable
                data={departments}
                columns={columns}
                currentPage={currentPage}
                pageSize={pageSize}
                totalCount={totalCount}
                totalPages={totalPages}
                loading={isLoading}
                sortBy={sortBy}
                sortDescending={sortDescending}
                onSort={handleSort}
                onPageChange={handlePageChange}
                onPageSizeChange={(size) => setPageSize(size)}
                onRefresh={loadDepartments}
                getRowKey={(d) => d.id}
                actionsRenderer={(d) => (
                  <div className="flex justify-end gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEdit(d)}
                      className="h-8 w-8 p-0 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400"
                      title="Edit Department"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(d.id)}
                      className="h-8 w-8 p-0 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400"
                      title="Delete Department"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
                emptyMessage={
                  searchQuery
                    ? "No departments match your search"
                    : "No departments found"
                }
              />
            );
          })()}
        </CardContent>
      </Card>
    </div>
  );
}
